import "./list-card-series.scss";

import { useUserIdentityContext } from "@core/services/user-identity.context";
import TagColor from "@core/ui/tag-color/tag-color";
import { useQyGetCardSeries } from "@factory/query/card-series.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import TextBlock from "@shared/ui/text-block/text-block";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { FilterCardSeries } from "../filter-card-series/filter-card-series";
import { useFilterCardSeriesContext } from "../filter-card-series/filter-card-series.context";

export function ListCardSeries() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const { userRoleIsAdmin } = useUserIdentityContext();
  const { formatDateLocal } = useDateTimeContext();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterCardSeriesContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: cardSeriesResponse,
    isLoading,
    isError,
    error,
  } = useQyGetCardSeries(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (code: any) => {
    navigate(`../customer/${code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleActivate = () => {
    navigate(`activate`);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        {userRoleIsAdmin() ? (
          <Button
            label="Filter"
            severity="secondary"
            badgeClassName="p-badge-danger"
            onClick={() => setFilterPanel(true)}
            outlined
          />
        ) : null}
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterCardSeries />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{cardSeriesResponse?.count}</b>
      </h5>

      <DataTable
        value={cardSeriesResponse?.data}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="card_alias" header="Alias" sortable></Column>
        <Column field="card_category_name" header="Category" sortable></Column>
        <Column
          field="activation_date"
          body={(cell) => formatDateLocal(cell.activation_date)}
          header="Activation"
          sortable
        ></Column>
        <Column
          field="is_active"
          body={(cell) => <TagColor value={cell.is_active} />}
          header="Active"
          sortable
        ></Column>
        <Column
          field="is_blocked"
          body={(cell) => <TagColor value={cell.is_blocked} />}
          header="Blocked"
          sortable
        ></Column>
        <Column
          field="is_lost"
          body={(cell) => <TagColor value={cell.is_lost} />}
          header="Lost"
          sortable
        ></Column>
        <Column
          field="is_stolen"
          body={(cell) => <TagColor value={cell.is_stolen} />}
          header="Stolen"
          sortable
        ></Column>
        <Column
          field="user_code"
          body={(cell) =>
            cell.user_code ? (
              <span
                className="cursor-pointer"
                onClick={() => navigateToRecord(cell.user_code)}
              >
                <TextBlock
                  label={cell.user_code}
                  value={`${cell.card_owner_first_name} ${cell.card_owner_last_name}`}
                  mode="vertical"
                />
              </span>
            ) : (
              <span>-</span>
            )
          }
          header="User"
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={cardSeriesResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListCardSeries" className="card-series">
      <HeaderContent title={<h1>Card Series</h1>}>
        <span className="flex gap-2">
          <Button label="Activate" onClick={handleActivate} />
          {userRoleIsAdmin() ? (
            <Button label="New" onClick={handleNew} outlined />
          ) : null}
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListCardSeries;
